import { isSuccess } from '@/context/services/type';
import { useQueryClient } from '@tanstack/react-query';
import {
  getGetApiJsApBusinessDocumentSettingQueryKey,
  PutApiJsApBusinessDocumentSettingMutationError,
  useGetApiJsApBusinessDocumentSettingSuspense,
  usePutApiJsApBusinessDocumentSetting,
} from 'ap-openapi';
import { useCallback, useMemo } from 'react';

// Types
export type BusinessDocumentSettingResponse = {
  is_enabled?: boolean;
  can_turn_on?: boolean;
};

type UpdateBusinessDocumentSettingResult =
  | { status: 'success' }
  | { status: 'error'; errors: PutApiJsApBusinessDocumentSettingMutationError };

export const useGetBusinessDocumentSetting = () => {
  const { data, error, isLoading } =
    useGetApiJsApBusinessDocumentSettingSuspense();
  if (error) throw error;
  return useMemo(
    () => ({
      data: data?.data,
      isLoading,
    }),
    [data, isLoading]
  );
};

export const useUpdateBusinessDocumentSetting = () => {
  const queryClient = useQueryClient();
  const { mutateAsync: _mutateAsync } = usePutApiJsApBusinessDocumentSetting();

  const mutateAsync = useCallback(
    async (
      isEnabled: boolean
    ): Promise<UpdateBusinessDocumentSettingResult> => {
      const resp = await _mutateAsync({
        data: {
          ap_business_document_setting: {
            is_enabled: isEnabled,
          },
        },
      });

      if (isSuccess(resp.status)) {
        await queryClient.invalidateQueries({
          queryKey: getGetApiJsApBusinessDocumentSettingQueryKey(),
        });
        return { status: 'success' };
      } else {
        return {
          status: 'error',
          // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
          errors: {} as PutApiJsApBusinessDocumentSettingMutationError,
        };
      }
    },
    [_mutateAsync, queryClient]
  );

  return { mutateAsync };
};

export const useBusinessDocumentSettingService = () => {
  const { data } = useGetBusinessDocumentSetting();
  const { mutateAsync: updateSetting } = useUpdateBusinessDocumentSetting();

  const enableSetting = useCallback(async () => {
    const result = await updateSetting(true);
    return result;
  }, [updateSetting]);

  const disableSetting = useCallback(async () => {
    const result = await updateSetting(false);
    return result;
  }, [updateSetting]);

  return {
    isEnabled: data?.is_enabled ?? false,
    enableSetting,
    disableSetting,
  };
};
